import React from 'react';
import { Button, useAlert } from '@uplift/ui-v2';
import { StateExhibitExpired, StateExhibitExpiring } from '@uplift/api';
import { useProviderLicensesStatusQuery, useRenewStateExhibitMutation, useStateExhibitTemplateQuery, useStateQuery } from '@uplift/queries';
import { Box, Divider, Table, TableBody, TableRow, Typography } from '@mui/material';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { AgreementText, DetailsCell, DocumentLayout } from './components';


export const StateExhibitRenewPreview: React.VFC<{
  document: StateExhibitExpiring | StateExhibitExpired;
  onSign: (stateExhibitId: string) => void;
}> = ({
  document,
  onSign,
}) => {
  const me = useCurrentuser();
  const alert = useAlert();
  const { data: exhibitTemplate } = useStateExhibitTemplateQuery(me.id, document.licenseId);
  const { data: licenses } = useProviderLicensesStatusQuery(me.id, { suspense: true });
  const license = licenses?.find(l => l.id === document.licenseId);
  const { data: state } = useStateQuery(license?.stateId, { suspense: true });
  const renewMutation = useRenewStateExhibitMutation(me.id, document.licenseId);

  const onRenew = async () => {
    if (!exhibitTemplate) {
      return;
    }

    try {
      const renewed = await renewMutation.mutateAsync({
        templateId: exhibitTemplate.template.id,
        version: exhibitTemplate.version.version,
      });

      alert.success('Your state exhibit has been renewed');
      onSign(renewed.data.id);
    } catch (err) {
      alert.error(err.message);
    }
  };

  return (
    <DocumentLayout
      title={exhibitTemplate?.template.name ?? 'State Exhibit'}
      subtitle="Sent by BetterHelp"
      controls={(
        <Box>
          <Divider />
          <Box sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!exhibitTemplate || renewMutation.isLoading}
              onClick={onRenew}
            >
              {renewMutation.isLoading ? 'Renewing...' : 'Sign Renewal'}
            </Button>
          </Box>
        </Box>
      )}
    >
      <Typography variant="overline" color="secondary">AGREEMENT DETAILS</Typography>
      <Table size="small" sx={{ 'tr:last-of-type td': { borderBottom: 0 } }}>
        <TableBody>
          <TableRow>
            <DetailsCell label="State Covered">{state?.fullName ?? '-'}</DetailsCell>
            <DetailsCell label="Status">
              {'expiredAt' in document
                ? (
                  <Typography color="error" fontWeight={500}>
                    Expired at {CalendarDateService.date(document.expiredAt, DateFormats.ISO_DATE).format(DateFormats['MMM D, YYYY'])}
                  </Typography>
                )
                : (
                  <Typography color="warning" fontWeight={500}>
                    Expiring at {CalendarDateService.date(document.expireAt, DateFormats.ISO_DATE).format(DateFormats['MMM D, YYYY'])}
                  </Typography>
                )}
            </DetailsCell>
          </TableRow>
        </TableBody>
      </Table>

      <AgreementText content={exhibitTemplate?.version.content ?? ''} />
    </DocumentLayout>
  );
};
